"use client";

import { useMemo, useRef, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, CupSoda, Droplets, Drumstick, Gem, Search, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  CATALOGUE_STATS,
  DIWLINE_EDITIONS,
  DIWLINE_FAMILLES,
  DIWLINE_GAMMES,
  DIWLINE_REFS,
  DIWUTIR_FORMATS,
  DIWUTIR_MARQUES,
  DIWUTIR_REFS,
  POULET_GAMMES,
  POULET_REFS,
  type RefDiwline,
  type RefDiwutir,
  type RefPoulet,
} from "@/lib/catalogue-data";
import { MAISON } from "@/lib/diwline-data";

/* ============================================================
   LE CATALOGUE ROYAL — 671 Références d'Exception
   Huiles & Beurres DIW'LINE (336) · LE POULET PARFAIT (36)
   · DiwuTir Bouteilles & Canettes (299) — visuels étiquetés
   par le Moteur d'Étiquetage Royal de la Maison.
   ============================================================ */

const PER_PAGE = 24;

type Onglet = "diwline" | "poulet" | "diwutir";

interface VM {
  code: string;
  titre: string;
  image: string;
  h: number;
  line1: string;
  line2: string;
  desc: string;
  chips: string[];
  alt: string;
}

const ONGLETS: { id: Onglet; label: string; court: string; count: number; icon: typeof Droplets; intro: string }[] = [
  {
    id: "diwline",
    label: "Huiles & Beurres DIW'LINE",
    court: "DIW'LINE",
    count: CATALOGUE_STATS.diwline,
    icon: Droplets,
    intro:
      "Quatre familles d'or liquide, trois gammes de cour, sept contenants d'apparat et quatre éditions royales : 336 références scellées sous le cachet de la Maison, de notre huilerie de Dakar aux tables du monde.",
  },
  {
    id: "poulet",
    label: "Le Poulet Parfait",
    court: "Poulet Parfait",
    count: CATALOGUE_STATS.poulet,
    icon: Drumstick,
    intro:
      "L'Empire Royal de Sa Majesté Fatima, Première Dame de l'Aviculture Africaine : six coupes d'honneur et six formats de barquette — 36 références bénies du grain doré du Sénégal.",
  },
  {
    id: "diwutir",
    label: "DiwuTir — Bouteilles & Canettes",
    court: "DiwuTir",
    count: CATALOGUE_STATS.diwutir,
    icon: CupSoda,
    intro:
      "La Maison de bouteilles et canettes DiwuTir et ses 23 marques impériales — de « Sunu Naggi » aux routes de soie sino-africaines : 299 références pétillantes dédiées aux tables du Sénégal, de l'Oummah et du monde.",
  },
];

function versVM(o: Onglet, r: RefDiwline | RefPoulet | RefDiwutir): VM {
  if (o === "diwline") {
    const d = r as RefDiwline;
    return {
      code: d.code, titre: d.titre, image: d.image, h: d.h,
      line1: d.contenant, line2: d.edition, desc: d.description,
      chips: [d.famille, `Gamme ${d.gamme}`, d.contenant, d.edition],
      alt: `${d.titre} — ${d.contenant}, ${d.edition} (référence ${d.code})`,
    };
  }
  if (o === "poulet") {
    const p = r as RefPoulet;
    return {
      code: p.code, titre: p.titre, image: p.image, h: p.h,
      line1: p.format, line2: p.note, desc: p.description,
      chips: [p.gamme, p.format, p.note],
      alt: `${p.titre} — ${p.format} (référence ${p.code})`,
    };
  }
  const t = r as RefDiwutir;
  return {
    code: t.code, titre: t.titre, image: t.image, h: t.h,
    line1: t.format, line2: t.dedicatee, desc: t.description,
    chips: [`Marque ${t.marque}`, t.famille, t.format, t.dedicatee],
    alt: `${t.titre} — ${t.format}, ${t.famille} (référence ${t.code})`,
  };
}

function SelectRoyal({
  valeur, onChange, options, libelle, toutLabel,
}: {
  valeur: string;
  onChange: (v: string) => void;
  options: string[];
  libelle: string;
  toutLabel: string;
}) {
  return (
    <label className="flex min-w-0 items-center gap-2">
      <span className="sr-only">{libelle}</span>
      <select
        value={valeur}
        onChange={e => onChange(e.target.value)}
        aria-label={libelle}
        className="h-10 w-full min-w-[9rem] rounded-sm border border-gold/30 bg-royal-panel px-3 text-[12.5px] text-cream outline-none transition-colors focus:border-gold"
      >
        <option value="*">{toutLabel}</option>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

export function CatalogueRoyal() {
  const [onglet, setOnglet] = useState<Onglet>("diwline");
  const [famille, setFamille] = useState("*");
  const [gamme, setGamme] = useState("*");
  const [edition, setEdition] = useState("*");
  const [marque, setMarque] = useState("*");
  const [format, setFormat] = useState("*");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [sel, setSel] = useState<VM | null>(null);
  const grilleRef = useRef<HTMLDivElement>(null);

  const filtres = { famille, gamme, edition, marque, format, q };

  const resetFiltres = () => {
    setFamille("*"); setGamme("*"); setEdition("*"); setMarque("*"); setFormat("*"); setQ("");
  };

  const changerOnglet = (id: Onglet) => {
    setOnglet(id);
    resetFiltres();
    setPage(0);
  };

  const majFiltre = (setter: (v: string) => void) => (v: string) => { setter(v); setPage(0); };

  const liste = useMemo<VM[]>(() => {
    const norm = q.trim().toLowerCase();
    let src: { r: RefDiwline | RefPoulet | RefDiwutir; ok: boolean }[];
    if (onglet === "diwline") {
      src = DIWLINE_REFS.map(r => ({
        r,
        ok:
          (famille === "*" || r.famille === famille) &&
          (gamme === "*" || r.gamme === gamme) &&
          (edition === "*" || r.edition === edition) &&
          (!norm || `${r.titre} ${r.contenant} ${r.edition} ${r.code}`.toLowerCase().includes(norm)),
      }));
    } else if (onglet === "poulet") {
      src = POULET_REFS.map(r => ({
        r,
        ok:
          (gamme === "*" || r.gamme === gamme) &&
          (!norm || `${r.titre} ${r.format} ${r.note} ${r.code}`.toLowerCase().includes(norm)),
      }));
    } else {
      src = DIWUTIR_REFS.map(r => ({
        r,
        ok:
          (marque === "*" || r.marque === marque) &&
          (format === "*" || r.format === format) &&
          (!norm || `${r.titre} ${r.dedicatee} ${r.famille} ${r.code}`.toLowerCase().includes(norm)),
      }));
    }
    return src.filter(s => s.ok).map(s => versVM(onglet, s.r));
  }, [onglet, famille, gamme, edition, marque, format, q]);

  const pages = Math.max(1, Math.ceil(liste.length / PER_PAGE));
  const pageOk = Math.min(page, pages - 1);
  const visibles = liste.slice(pageOk * PER_PAGE, pageOk * PER_PAGE + PER_PAGE);

  const allerPage = (p: number) => {
    setPage(Math.max(0, Math.min(pages - 1, p)));
    grilleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ongletActif = ONGLETS.find(t => t.id === onglet)!;

  return (
    <section id="catalogue" aria-label="Le Catalogue Royal" className="relative scroll-mt-20 bg-royal py-20 md:py-28">
      <div className="bg-arabesque pointer-events-none absolute inset-0" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center">
          <p className="font-elegant text-lg italic text-gold">Le Trésor de la Couronne — {CATALOGUE_STATS.total} Références</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-wide text-gold-gradient animate-shimmer md:text-5xl">
            Le Catalogue Royal
          </h2>
          <div className="mx-auto mt-5 flex items-center justify-center gap-3" aria-hidden>
            <span className="h-px w-16 bg-gradient-to-r from-transparent to-gold/70" />
            <Gem className="h-4 w-4 text-gold" />
            <span className="h-px w-16 bg-gradient-to-l from-transparent to-gold/70" />
          </div>
          <p className="mx-auto mt-5 max-w-3xl text-[14.5px] leading-relaxed text-cream/70">
            À la limite de l&apos;ingéniosité et de la technologie, la Maison compose chaque visuel produit par son
            <span className="text-gold"> Moteur d&apos;Étiquetage Royal</span> : maîtres photoréalistes d&apos;exception,
            étiquettes de luxe à la typographie élégante, rendu commercial digne d&apos;un patrimoine de cour.
          </p>
        </div>

        {/* Statistiques */}
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { n: CATALOGUE_STATS.diwline, l: "Huiles & Beurres" },
            { n: CATALOGUE_STATS.poulet, l: "Poulet Parfait" },
            { n: CATALOGUE_STATS.diwutir, l: "Bouteilles & Canettes" },
            { n: CATALOGUE_STATS.joudi, l: "Éditions JOUDI" },
          ].map(s => (
            <div key={s.l} className="gold-frame rounded-sm bg-royal-panel/80 px-4 py-4 text-center">
              <p className="font-display text-2xl font-bold text-gold-gradient md:text-3xl">{s.n}</p>
              <p className="mt-1 text-[10.5px] uppercase tracking-[0.18em] text-cream/60">{s.l}</p>
            </div>
          ))}
        </div>

        {/* Onglets */}
        <div role="tablist" aria-label="Familles du catalogue" className="mt-12 flex flex-wrap justify-center gap-3">
          {ONGLETS.map(t => {
            const Icone = t.icon;
            const actif = t.id === onglet;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={actif}
                onClick={() => changerOnglet(t.id)}
                className={`inline-flex min-h-11 items-center gap-2.5 rounded-sm border px-5 py-2.5 text-[12.5px] font-semibold uppercase tracking-[0.14em] transition-all ${
                  actif
                    ? "border-gold bg-gold text-royal shadow-[0_10px_35px_rgba(212,175,55,0.25)]"
                    : "border-gold/35 bg-royal-panel/70 text-cream/80 hover:border-gold/70 hover:text-gold"
                }`}
              >
                <Icone className="h-4.5 w-4.5 shrink-0" aria-hidden />
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden">{t.court}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${actif ? "bg-royal/15 text-royal" : "bg-gold/15 text-gold"}`}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Intro de l'onglet */}
        <motion.p
          key={onglet}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto mt-8 max-w-3xl text-center text-[13.5px] leading-relaxed text-cream/65"
        >
          {ongletActif.intro}
        </motion.p>

        {/* Filtres */}
        <div className="mx-auto mt-8 flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          {onglet === "diwline" && (
            <>
              <SelectRoyal libelle="Famille" valeur={famille} onChange={majFiltre(setFamille)} options={DIWLINE_FAMILLES} toutLabel="Toutes les familles" />
              <SelectRoyal libelle="Gamme" valeur={gamme} onChange={majFiltre(setGamme)} options={DIWLINE_GAMMES} toutLabel="Toutes les gammes" />
              <SelectRoyal libelle="Édition" valeur={edition} onChange={majFiltre(setEdition)} options={DIWLINE_EDITIONS} toutLabel="Toutes les éditions" />
            </>
          )}
          {onglet === "poulet" && (
            <SelectRoyal libelle="Coupe royale" valeur={gamme} onChange={majFiltre(setGamme)} options={POULET_GAMMES} toutLabel="Toutes les coupes" />
          )}
          {onglet === "diwutir" && (
            <>
              <SelectRoyal libelle="Marque" valeur={marque} onChange={majFiltre(setMarque)} options={DIWUTIR_MARQUES} toutLabel="Toutes les marques" />
              <SelectRoyal libelle="Format" valeur={format} onChange={majFiltre(setFormat)} options={DIWUTIR_FORMATS} toutLabel="Tous les formats" />
            </>
          )}
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gold/70" aria-hidden />
            <Input
              value={q}
              onChange={e => { setQ(e.target.value); setPage(0); }}
              placeholder="Rechercher une référence…"
              aria-label="Rechercher une référence du catalogue"
              className="h-10 rounded-sm border-gold/30 bg-royal-panel pl-9 text-[13px] text-cream placeholder:text-cream/40 focus-visible:ring-gold/50"
            />
          </div>
        </div>

        {/* Compteur + pagination haute */}
        <div ref={grilleRef} className="mt-10 flex scroll-mt-24 flex-wrap items-center justify-between gap-3">
          <p className="text-[12px] uppercase tracking-[0.2em] text-cream/55">
            {liste.length} référence{liste.length > 1 ? "s" : ""} — Page {pageOk + 1} / {pages}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={pageOk === 0} onClick={() => allerPage(pageOk - 1)}
              className="h-9 border-gold/40 bg-transparent text-gold hover:bg-gold hover:text-royal disabled:opacity-30"
              aria-label="Page précédente">
              <ChevronLeft className="h-4 w-4" aria-hidden /> Précédente
            </Button>
            <Button variant="outline" size="sm" disabled={pageOk >= pages - 1} onClick={() => allerPage(pageOk + 1)}
              className="h-9 border-gold/40 bg-transparent text-gold hover:bg-gold hover:text-royal disabled:opacity-30"
              aria-label="Page suivante">
              Suivante <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>

        {/* Grille des références */}
        <motion.div
          key={`${onglet}-${pageOk}-${liste.length}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
          role="list"
        >
          {visibles.map(r => (
            <button
              key={r.code}
              role="listitem"
              onClick={() => setSel(r)}
              aria-label={`Ouvrir la fiche royale de ${r.titre}, référence ${r.code}`}
              className="group relative overflow-hidden rounded-sm border border-gold/25 bg-royal-panel text-left transition-all duration-300 hover:-translate-y-1 hover:border-gold/70 hover:shadow-[0_18px_50px_rgba(0,0,0,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
            >
              <Image
                src={r.image}
                alt={r.alt}
                width={720}
                height={r.h}
                loading="lazy"
                className="w-full transition-transform duration-500 group-hover:scale-[1.05]"
              />
              <span className="flex items-center justify-between gap-2 border-t border-gold/20 bg-royal/85 px-2.5 py-2">
                <span className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-gold">{r.code}</span>
                <span className="truncate text-[10.5px] text-cream/70">{r.line1}</span>
              </span>
              <span className="pointer-events-none absolute inset-0 border border-gold/0 transition-colors group-hover:border-gold/50" aria-hidden />
            </button>
          ))}
        </motion.div>

        {visibles.length === 0 && (
          <p className="mt-10 text-center text-sm text-cream/60">
            Aucune référence ne répond à cette requête — que la recherche reprenne, la Maison veille.
          </p>
        )}

        {/* Pagination basse */}
        <div className="mt-10 flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={pageOk === 0} onClick={() => allerPage(pageOk - 1)}
            className="h-10 border-gold/40 bg-transparent text-gold hover:bg-gold hover:text-royal disabled:opacity-30"
            aria-label="Page précédente">
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <ScrollText className="h-4 w-4 text-gold/60" aria-hidden />
          <p className="font-elegant text-sm italic text-gold/85">
            Feuillet {pageOk + 1} sur {pages} du Grand Registre
          </p>
          <Button variant="outline" size="sm" disabled={pageOk >= pages - 1} onClick={() => allerPage(pageOk + 1)}
            className="h-10 border-gold/40 bg-transparent text-gold hover:bg-gold hover:text-royal disabled:opacity-30"
            aria-label="Page suivante">
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>

      {/* Fiche produit royale */}
      <Dialog open={!!sel} onOpenChange={o => !o && setSel(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-gold/35 bg-royal-brown text-cream scrollbar-royale md:max-w-3xl">
          {sel && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="gold-frame overflow-hidden rounded-sm">
                <Image src={sel.image} alt={sel.alt} width={720} height={sel.h} className="w-full" />
              </div>
              <div className="flex flex-col">
                <DialogHeader className="text-left">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.3em] text-gold">{sel.code} — Fiche Royale</p>
                  <DialogTitle className="font-display text-2xl font-bold leading-snug text-gold-gradient">
                    {sel.titre}
                  </DialogTitle>
                  <DialogDescription className="pt-1 font-elegant text-[15px] italic text-cream/75">
                    {sel.line1} · {sel.line2}
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-3 flex flex-wrap gap-2">
                  {sel.chips.map(c => (
                    <span key={c} className="rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[10.5px] uppercase tracking-[0.12em] text-gold-light">
                      {c}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-[13.5px] leading-relaxed text-cream/75">{sel.desc}</p>
                <div className="mt-auto pt-6">
                  <p className="rounded-sm border border-gold/20 bg-royal-panel/80 p-3.5 text-[12px] leading-relaxed text-cream/65">
                    <span className="text-gold">Cachet de la Maison —</span> chaque référence est scellée par les
                    techniciens royaux et enregistrée au Grand Registre du Catalogue. Commande auprès du Comptoir
                    Royal — {MAISON.ville}.
                  </p>
                  <a
                    href="#contact"
                    onClick={() => setSel(null)}
                    className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-sm border border-gold bg-gold px-6 text-[12px] font-semibold uppercase tracking-[0.22em] text-royal transition-all hover:bg-transparent hover:text-gold"
                  >
                    Demander audience au Comptoir
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
